import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layouts/Layout';
import './EmployeeProfile.css';
import { FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { FaEnvelope, FaPhone, FaCalendarAlt, FaBuilding, FaBriefcase, FaMapMarkerAlt, FaIdCard, FaUserCircle } from 'react-icons/fa';

const EmployeeProfile = () => {
    const baseUrl = "https://api.burrayhr.com";
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Profile data:', response.data);
      setProfile(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateRemainingTime = (endDate) => {
    if (!endDate) return null;
    
    const today = new Date();
    const contractEnd = new Date(endDate);
    
    // If contract has expired
    if (contractEnd < today) {
      return 'Contract Expired';
    }
    
    const timeDiff = contractEnd - today;
    const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} remaining`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? 's' : ''} remaining`;
    } else {
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="profile-loading">
          <FaSpinner className="spinner" />
          <p>Loading your profile...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="profile-error">
          <FaExclamationTriangle className="error-icon" />
          <h3>Error Loading Profile</h3>
          <p>{error}</p>
          <button onClick={fetchProfile} className="retry-button">Try Again</button>
        </div>
      </Layout>
    );
  }

  // Extract contract details
  const contractType = profile?.contracts[0]?.contract_type || profile?.contract_details?.type;
  const contractStartDate = profile?.contracts[0]?.start_date || profile?.contract_details?.start_date;
  const contractEndDate = profile?.contracts[0]?.end_date || profile?.contract_details?.end_date;
  const isContractActive = profile?.contracts[0]?.is_active || profile?.is_active;

  return (
    <Layout>
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-photo-container">
              {profile?.employee_data?.photo ? (
                <img 
                  src={profile?.employee_data?.photo} 
                  alt={`${profile.first_name}'s profile`}
                  className="profile-photo"
                />
              ) : (
                <FaUserCircle className="profile-photo-placeholder" />
              )}
            </div>
            <div className="profile-title">
              <h1 className="profile-name">{profile?.employee_data?.full_name}</h1>
              <p className="profile-position">
                <span style={{fontWeight: 'bold'}}>Position:</span> {profile?.position || profile?.employee_data?.position || 'Employee'}
              </p>
              <div className="profile-id">
                
                <span>Employee ID: {profile?.employee_data?.employee_id || 'Not assigned'}</span>
              </div>
            </div>
          </div>
          <div className="profile-status">
            <span className={`status-badge ${isContractActive ? 'active' : 'inactive'}`}>
              {isContractActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-section">
            <h2 className="section-title">
              <FaUserCircle style={{color: 'navy'}} className="" />
              Employee Information
            </h2>
            <div className="info-grid">
              <div className="info-item">
                <FaEnvelope className="info-icon" />
                <div>
                  <label>Email</label>
                  <p style={{fontSize: '12px'}}>{profile?.email || 'Not provided'}</p>
                </div>
              </div>
              <div className="info-item">
                <FaPhone className="info-icon" />
                <div>
                  <label>Phone</label>
                  <p>{profile?.phone_number || profile?.employee_data?.phone_number || 'Not provided'}</p>
                </div>
              </div>
              <div className="info-item">
                <FaCalendarAlt className="info-icon" />
                <div>
                  <label>Date of Birth</label>
                  <p>{formatDate(profile?.date_of_birth || profile?.employee_data?.date_of_birth)}</p>
                </div>
              </div>
              <div className="info-item">
                <FaBuilding className="info-icon" />
                <div>
                  <label>Department</label>
                  <p>{profile?.department_name || profile?.employee_data?.department || 'Not assigned'}</p>
                </div>
              </div>
              
            </div>
          </div>
          <div className="profile-section">
            <h2 className="section-title">
              <FaIdCard style={{color: 'navy'}} className="" />
              Contract Information
            </h2>
            <div className="contract-card">
              <div className="contract-header">
                <h3>{contractType ? contractType.replace(/_/g, ' ').toUpperCase() : 'Employment Contract'}</h3>
                <span className={`contract-status ${isContractActive ? 'active' : 'inactive'}`}>
                  {isContractActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="contract-details">
                <div className="contract-item">
                  <label>Start Date</label>
                  <p>{formatDate(contractStartDate)}</p>
                </div>
                
                {contractType === 'fixed_term' && (
                  <>
                    <div className="contract-item">
                      <label>End Date</label>
                      <p>{formatDate(contractEndDate)}</p>
                    </div>
                    {contractEndDate && (
                      <div className="contract-item">
                        <label>Time Remaining</label>
                        <p className={calculateRemainingTime(contractEndDate) === 'Contract Expired' ? 'expired' : ''}>
                          {calculateRemainingTime(contractEndDate)}
                        </p>
                      </div>
                    )}
                  </>
                )}
                
                {contractType !== 'fixed_term' && (
                  <div className="contract-item">
                    <label>Contract Status</label>
                    <div className="contract-status-indicator">
                      {isContractActive ? (
                        <>
                          <FaCheckCircle className="status-icon active" />
                          <span>Open Contract (Active)</span>
                        </>
                      ) : (
                        <>
                          <FaTimesCircle className="status-icon inactive" />
                          <span>Open Contract (Inactive)</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeProfile;
